import React, { useMemo } from 'react';
import PrintDocument, {
  DocSection, DocFieldGrid, DocField, DocTable, DocMark, formatDate,
} from '../PrintDocument';
import { sectionLabel, personName, formatRegNo } from '../labels';
import './documents.css';

/** The mark at or above which a pupil passes. Not a schema value —
 *  nothing in the database records a pass threshold — so it is a prop
 *  with the conventional 50% as its default, and the document prints
 *  whatever it is told. */
const DEFAULT_PASS_PERCENT = 50;

/**
 * MarksSheetDoc — كشف علامات.
 *
 * The pupil's transcript: every subject down the side, every exam kind
 * across the top, the mark in each cell, then totals, average and
 * result.
 *
 * WHY THE PIVOT HAPPENS HERE
 * `api.studentReportCard(id)` (route exams.js studentReportCard)
 * returns a FLAT list of exam-result rows, one per exam sat, ordered
 * by date — not a grid. The grid is a presentation choice, so this
 * component builds it: subjects become rows, exam_type_id becomes
 * columns. exam_type_id is the correct column key and NOT exam_title:
 * the route comments say so explicitly — the exam kind ("مذاكرة أولى")
 * is school-wide and means the same thing in every section, while an
 * exam's own title is per-section and would produce one column per
 * section the pupil has ever been in.
 *
 * WHY MARKS ARE SHOWN AS "obtained / total"
 * total_marks differs per exam (a quiz out of 10, a final out of 100),
 * so a bare "8" is unreadable. Each cell prints the denominator with
 * it, and the totals row sums both columns so the average is honest.
 *
 * WHY A MISSED EXAM IS A DASH, NOT A ZERO
 * A pupil who did not sit an exam has no row in exam_results at all.
 * Printing 0 would defame them; the cell prints a dash and the exam
 * is excluded from the totals, so the average reflects what they
 * actually sat.
 *
 * PROPS CONTRACT
 *   student    object  REQUIRED. api.student(id) or a studentDirectory
 *                      row. Fields read: name, registration_number,
 *                      national_id, section_id, section_label,
 *                      grade_title, birth_date
 *   results    array   REQUIRED (may be empty). Exactly what
 *                      `api.studentReportCard(id)` returns. Each row:
 *                        exam_id, exam_title, date, semester,
 *                        exam_type_id, exam_type_title,
 *                        subject_id, subject_title, weight,
 *                        section_title_at_the_time,
 *                        section_label_at_the_time,
 *                        marks_obtained, total_marks, percentage
 *   semester   string|number? when given, only results from that
 *                      semester are printed and it is named in the
 *                      subtitle. Omit for a full transcript.
 *   academicYear string? printed in the subtitle.
 *   passPercent number? pass threshold, default 50.
 *   issuedAt   string?  ISO date; defaults to today.
 *   docNumber  string|number? reference shown in the letterhead.
 *   logoUrl    string?  school logo; a placeholder box prints without it.
 *   teacherName / principalName  string? printed under the signature lines.
 *   showToolbar / onClose / autoPrint — forwarded to PrintDocument.
 *
 * PURE / PRESENTATIONAL: fetches nothing, owns no state.
 */
export default function MarksSheetDoc({
  student,
  results,
  semester,
  academicYear,
  passPercent = DEFAULT_PASS_PERCENT,
  issuedAt,
  docNumber,
  logoUrl,
  teacherName,
  principalName,
  showToolbar = true,
  onClose,
  autoPrint = false,
}) {
  const rows = useMemo(() => (results || []).filter(
    (r) => semester === undefined || semester === null || semester === ''
      || String(r.semester) === String(semester),
  ), [results, semester]);

  const grid = useMemo(() => buildGrid(rows), [rows]);

  if (!student) return null;

  const section = student.section_label || student.section_id ? sectionLabel(student) : null;

  const overallPercent = grid.totalPossible > 0
    ? (100 * grid.totalObtained) / grid.totalPossible
    : null;
  const passed = overallPercent !== null && overallPercent >= passPercent;

  const subtitleParts = [];
  if (academicYear) subtitleParts.push(`العام الدراسي ${academicYear}`);
  if (semester !== undefined && semester !== null && semester !== '') {
    subtitleParts.push(`الفصل ${semester}`);
  }

  return (
    <PrintDocument
      title="كشف علامات"
      subtitle={subtitleParts.length ? subtitleParts.join(' — ') : undefined}
      docNumber={docNumber ?? (formatRegNo(student.registration_number) || undefined)}
      issuedAt={issuedAt}
      logoUrl={logoUrl}
      schoolSubtitle="قسم الامتحانات"
      showToolbar={showToolbar}
      onClose={onClose}
      autoPrint={autoPrint}
      footerNote="كشف العلامات غير معتمد ما لم يكن مختوماً بخاتم المدرسة."
      signatures={[
        { title: 'المدرّس المسؤول', name: teacherName || undefined },
        { title: 'مدير المدرسة', name: principalName || undefined },
      ]}
    >
      <DocSection>
        <div className="ui-docx-marks-head">
          <DocFieldGrid columns={2}>
            <DocField label="اسم الطالب" value={personName(student)} wide />
            <DocField label="الصف" value={student.grade_title} />
            <DocField label="الشعبة" value={section} />
            <DocField label="رقم التسجيل" value={formatRegNo(student.registration_number)} numeric />
            <DocField label="الرقم الوطني" value={student.national_id} numeric />
          </DocFieldGrid>
        </div>
      </DocSection>

      {grid.subjects.length === 0 ? (
        /* Said in words. A blank table on an official document reads as
           a printing fault, not as "no exams recorded". */
        <p className="ui-docx-empty">
          لا توجد علامات مسجّلة لهذا الطالب
          {semester !== undefined && semester !== null && semester !== '' ? ' في هذا الفصل' : ''}.
        </p>
      ) : (
        <>
          <DocTable
            compact
            head={(
              <tr>
                <th className="ui-docx-subject">المادة</th>
                {grid.columns.map((c) => (
                  <th key={c.key} className="is-num">{c.title}</th>
                ))}
                <th className="is-num">المجموع</th>
                <th className="is-num">النسبة</th>
              </tr>
            )}
          >
            {grid.subjects.map((s) => (
              <tr key={s.key}>
                <td className="ui-docx-subject">{s.title}</td>
                {grid.columns.map((c) => {
                  const cell = s.cells[c.key];
                  return (
                    <td key={c.key} className="is-num">
                      {cell
                        ? `${formatNumber(cell.obtained)} / ${formatNumber(cell.total)}`
                        : <span className="ui-docx-no-mark">—</span>}
                    </td>
                  );
                })}
                <td className="is-num">
                  {s.possible > 0
                    ? `${formatNumber(s.obtained)} / ${formatNumber(s.possible)}`
                    : <span className="ui-docx-no-mark">—</span>}
                </td>
                <td className="is-num">
                  {s.possible > 0 ? `${formatNumber((100 * s.obtained) / s.possible, 1)}%` : '—'}
                </td>
              </tr>
            ))}

            <tr className="is-total">
              <td className="ui-docx-subject">المجموع العام</td>
              {/* One empty cell per exam column — a colspan here would
                  misalign the two numeric columns that follow. */}
              {grid.columns.map((c) => <td key={c.key} className="is-num" />)}
              <td className="is-num">
                {formatNumber(grid.totalObtained)} / {formatNumber(grid.totalPossible)}
              </td>
              <td className="is-num">
                {overallPercent !== null ? `${formatNumber(overallPercent, 1)}%` : '—'}
              </td>
            </tr>
          </DocTable>

          <div className="ui-docx-summary">
            <div className="ui-docx-summary-item">
              <span className="ui-docx-summary-label">عدد المواد</span>
              <span className="ui-docx-summary-value ui-doc-num">{grid.subjects.length}</span>
            </div>
            <div className="ui-docx-summary-item">
              <span className="ui-docx-summary-label">المعدّل</span>
              <span className="ui-docx-summary-value ui-doc-num">
                {overallPercent !== null ? `${formatNumber(overallPercent, 2)}%` : '—'}
              </span>
            </div>
            <div className="ui-docx-summary-item">
              <span className="ui-docx-summary-label">النتيجة</span>
              {/* DocMark, not a coloured pill: the word carries the
                  meaning, because the printer is monochrome. */}
              <DocMark tone={overallPercent === null ? 'neutral' : passed ? 'success' : 'danger'}>
                {overallPercent === null ? '—' : passed ? 'ناجح' : 'راسب'}
              </DocMark>
            </div>
            <div className="ui-docx-summary-item">
              <span className="ui-docx-summary-label">درجة النجاح</span>
              <span className="ui-docx-summary-value ui-doc-num">{passPercent}%</span>
            </div>
          </div>

          {grid.lastExamDate && (
            <p className="ui-doc-foot-note" style={{ textAlign: 'start', border: 0 }}>
              آخر امتحان مسجّل بتاريخ <span className="ui-doc-num">{formatDate(grid.lastExamDate)}</span>
            </p>
          )}
        </>
      )}
    </PrintDocument>
  );
}

/**
 * Pivots the flat report-card rows into subjects × exam types.
 *
 * Column order follows first appearance, which — because the route
 * orders by exam date — is chronological: مذاكرة أولى before نصف
 * الفصل before النهائي, without this file hard-coding a list of exam
 * kinds it has no business knowing.
 *
 * Duplicate (subject, exam type) pairs are summed rather than the
 * later one overwriting the earlier: a pupil legitimately sits the
 * same kind of exam twice in a year in some subjects, and dropping one
 * would understate the total.
 */
function buildGrid(rows) {
  const columns = [];
  const columnSeen = new Set();
  const subjects = [];
  const subjectByKey = new Map();
  let totalObtained = 0;
  let totalPossible = 0;
  let lastExamDate = null;

  for (const r of rows) {
    // A result with no recorded mark is not a zero — it is an absence
    // of data, and it must not drag the average down.
    if (r.marks_obtained === null || r.marks_obtained === undefined) continue;

    const colKey = String(r.exam_type_id ?? r.exam_type_title ?? r.exam_id);
    if (!columnSeen.has(colKey)) {
      columnSeen.add(colKey);
      columns.push({ key: colKey, title: r.exam_type_title || r.exam_title || '—' });
    }

    const subKey = String(r.subject_id ?? r.subject_title);
    let subject = subjectByKey.get(subKey);
    if (!subject) {
      subject = { key: subKey, title: r.subject_title || '—', cells: {}, obtained: 0, possible: 0 };
      subjectByKey.set(subKey, subject);
      subjects.push(subject);
    }

    const obtained = Number(r.marks_obtained) || 0;
    const total = Number(r.total_marks) || 0;

    const existing = subject.cells[colKey];
    subject.cells[colKey] = existing
      ? { obtained: existing.obtained + obtained, total: existing.total + total }
      : { obtained, total };

    subject.obtained += obtained;
    subject.possible += total;
    totalObtained += obtained;
    totalPossible += total;

    if (r.date && (!lastExamDate || r.date > lastExamDate)) lastExamDate = r.date;
  }

  // Alphabetical by subject, so the same pupil's sheet has the same
  // row order every time it is reprinted.
  subjects.sort((a, b) => a.title.localeCompare(b.title, 'ar'));

  return { columns, subjects, totalObtained, totalPossible, lastExamDate };
}

/** Marks are often whole numbers; print 8 rather than 8.00, but keep
 *  the decimals when a half-mark was actually awarded. */
function formatNumber(value, decimals = 2) {
  const n = Number(value) || 0;
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(decimals).replace(/\.?0+$/, '');
}
